// Sync Service - Handles online/offline synchronization
// Queues operations when offline and syncs when online

import offlineDB from './offlineDatabase';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

class SyncService {
  constructor() {
    this.isOnline = navigator.onLine;
    this.isSyncing = false;
    this.listeners = new Set();
    this.syncInterval = null;
    
    // Listen for online/offline events
    window.addEventListener('online', () => this.handleOnline());
    window.addEventListener('offline', () => this.handleOffline());
    
    // Start periodic sync check
    this.startPeriodicSync();
  }

  // Subscribe to sync events
  subscribe(callback) {
    this.listeners.add(callback);
    return () => this.listeners.delete(callback);
  }

  notifyListeners(event) {
    this.listeners.forEach(callback => callback(event));
  }

  handleOnline() {
    this.isOnline = true;
    console.log('🌐 Back online!');
    this.notifyListeners({ type: 'online' });
    this.syncPendingOperations();
  }

  handleOffline() {
    this.isOnline = false;
    console.log('📴 Gone offline');
    this.notifyListeners({ type: 'offline' });
  }

  startPeriodicSync() {
    // Check and sync every 30 seconds when online
    this.syncInterval = setInterval(() => {
      if (this.isOnline && !this.isSyncing) {
        this.syncPendingOperations();
      }
    }, 30000);
  }

  stopPeriodicSync() {
    if (this.syncInterval) {
      clearInterval(this.syncInterval);
    }
  }

  // Queue an operation for sync
  async queueOperation(type, endpoint, method, data, metadata = {}) {
    const operation = {
      type,
      endpoint,
      method,
      data,
      metadata,
      createdAt: new Date().toISOString()
    };

    await offlineDB.addToSyncQueue(operation);
    console.log(`📥 Queued ${type} operation for sync`);
    
    this.notifyListeners({ 
      type: 'queued', 
      operation: type,
      pendingCount: (await offlineDB.getPendingSyncItems()).length 
    });

    // Try to sync immediately if online
    if (this.isOnline) {
      this.syncPendingOperations();
    }

    return { queued: true, offlineId: operation.id };
  }

  // Process a single sync operation
  async processOperation(item) {
    try {
      const response = await fetch(`${BACKEND_URL}${item.endpoint}`, {
        method: item.method,
        headers: {
          'Content-Type': 'application/json'
        },
        body: item.method !== 'GET' ? JSON.stringify(item.data) : undefined
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const result = await response.json();
      
      // Mark as synced and remove from queue
      await offlineDB.removeSyncItem(item.id);
      
      console.log(`✅ Synced: ${item.type}`);
      return { success: true, result };
      
    } catch (error) {
      console.error(`❌ Sync failed for ${item.type}:`, error.message);
      
      // Update retry count
      if (item.retryCount < item.maxRetries) {
        await offlineDB.updateSyncItemStatus(item.id, 'retry', error.message);
      } else {
        await offlineDB.updateSyncItemStatus(item.id, 'failed', error.message);
      }
      
      return { success: false, error: error.message };
    }
  }

  // Sync all pending operations
  async syncPendingOperations() {
    if (!this.isOnline || this.isSyncing) return;

    this.isSyncing = true;
    this.notifyListeners({ type: 'syncStart' });

    try {
      const pendingItems = await offlineDB.getPendingSyncItems();
      
      if (pendingItems.length === 0) {
        this.isSyncing = false;
        return;
      }

      console.log(`🔄 Syncing ${pendingItems.length} pending operations...`);
      
      let synced = 0;
      let failed = 0;

      // Process in order (oldest first)
      const sortedItems = pendingItems.sort((a, b) => a.timestamp - b.timestamp);
      
      for (const item of sortedItems) {
        const result = await this.processOperation(item);
        if (result.success) {
          synced++;
        } else {
          failed++;
        }
      }

      console.log(`📊 Sync complete: ${synced} synced, ${failed} failed`);
      
      this.notifyListeners({ 
        type: 'syncComplete', 
        synced, 
        failed,
        remaining: (await offlineDB.getPendingSyncItems()).length
      });

    } catch (error) {
      console.error('Sync error:', error);
      this.notifyListeners({ type: 'syncError', error: error.message });
    }

    this.isSyncing = false;
  }

  // Get pending operations count
  async getPendingCount() {
    const items = await offlineDB.getPendingSyncItems();
    return items.length;
  }

  // Get all pending operations (for display)
  async getPendingOperations() {
    return offlineDB.getPendingSyncItems();
  }

  // Force sync now
  async forcSync() {
    if (!this.isOnline) {
      return { success: false, error: 'No internet connection' };
    }
    await this.syncPendingOperations();
    return { success: true };
  }

  // Clear failed operations
  async clearFailedOperations() {
    const items = await offlineDB.getAll('syncQueue');
    for (const item of items) {
      if (item.status === 'failed') {
        await offlineDB.removeSyncItem(item.id);
      }
    }
  }
}

// Create singleton instance
const syncService = new SyncService();

export default syncService;
